import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiCreditCard, FiX, FiPlus, FiUpload } from 'react-icons/fi';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const emptyForm = () => ({ entity: '', bank_name: '', account_name: '', account_number: '', currency: '', ledger_account: '' });

const BankAccounts = () => {
  const [entities, setEntities] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const fileInputRef = useRef(null);
  const importTargetRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [entRes, curRes, accRes, bankRes] = await Promise.all([
        financeAPI.entities.list(), financeAPI.currencies.list(), financeAPI.accounts.list(), financeAPI.bankAccounts.list(),
      ]);
      setEntities(entRes.data.results || entRes.data || []);
      setCurrencies(curRes.data.results || curRes.data || []);
      setAccounts(accRes.data.results || accRes.data || []);
      setBankAccounts(bankRes.data.results || bankRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load bank accounts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await financeAPI.bankAccounts.create(form);
      setForm(emptyForm());
      setShowForm(false);
      load();
    } catch (e2) {
      setError(e2?.response?.data?.error || JSON.stringify(e2?.response?.data) || 'Failed to create bank account.');
    } finally {
      setSaving(false);
    }
  };

  const triggerImport = (bankAccount) => {
    importTargetRef.current = bankAccount.id;
    fileInputRef.current?.click();
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !importTargetRef.current) return;
    setImportingId(importTargetRef.current);
    setError(null);
    try {
      const res = await financeAPI.bankAccounts.importStatement(importTargetRef.current, file);
      window.alert(`Imported ${res.data.line_count} statement line(s) from ${res.data.source_filename}.`);
      load();
    } catch (e2) {
      setError(e2?.response?.data?.error || 'Failed to import statement.');
    } finally {
      setImportingId(null);
      importTargetRef.current = null;
    }
  };

  const entityName = (id) => entities.find((e) => e.id === id)?.name || `#${id}`;
  const accountLabel = (id) => { const a = accounts.find((x) => x.id === id); return a ? `${a.code} — ${a.name}` : `#${id}`; };

  return (
    <div style={fs.page}>
      <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={onFileChosen} />
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiCreditCard size={22} />Bank Accounts</h1>
          <p style={fs.pageSubtitle}>Company bank accounts and their ledger clearing account. Import a CSV statement to feed reconciliation on the Bank Statement Lines page.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowForm((v) => !v)} style={fs.primaryButton}><FiPlus size={14} />New Bank Account</button>
          <button onClick={load} title="Refresh" style={fs.iconButton}><FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /></button>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submitForm} style={{ ...fs.card, marginBottom: 22, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div><div style={fs.label}>Entity</div>
            <select required value={form.entity} onChange={(e) => setForm({ ...form, entity: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>{entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div><div style={fs.label}>Bank Name</div><input required value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
          <div><div style={fs.label}>Account Name</div><input required value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
          <div><div style={fs.label}>Account Number</div><input required value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
          <div><div style={fs.label}>Currency</div>
            <select required value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>{currencies.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
            </select>
          </div>
          <div><div style={fs.label}>Ledger (Clearing) Account</div>
            <select required value={form.ledger_account} onChange={(e) => setForm({ ...form, ledger_account: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>{accounts.filter((a) => !form.entity || String(a.entity) === String(form.entity)).map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" disabled={saving} style={fs.primaryButton}>{saving ? 'Saving…' : 'Create'}</button>
          </div>
        </form>
      )}

      {loading ? (
        <div style={fs.loadingState}>Loading…</div>
      ) : bankAccounts.length === 0 ? (
        <div style={fs.emptyState}><FiCreditCard size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No bank accounts yet.</div></div>
      ) : (
        <div style={fs.tableWrap}>
          <table style={fs.table}>
            <thead><tr style={fs.tableHeadRow}>{['Entity', 'Bank', 'Account', 'Number', 'Currency', 'Ledger Account', ''].map((h) => <th key={h} style={fs.th}>{h}</th>)}</tr></thead>
            <tbody>
              {bankAccounts.map((b, i) => (
                <tr key={b.id} style={{ borderBottom: i < bankAccounts.length - 1 ? '1px solid #111' : 'none' }}>
                  <td style={fs.td}>{entityName(b.entity)}</td>
                  <td style={{ ...fs.td, color: fs.colors.textBright }}>{b.bank_name}</td>
                  <td style={fs.td}>{b.account_name}</td>
                  <td style={fs.td}>{b.account_number_masked || '—'}</td>
                  <td style={fs.td}>{b.currency}</td>
                  <td style={fs.td}>{accountLabel(b.ledger_account)}</td>
                  <td style={fs.td}>
                    <button onClick={() => triggerImport(b)} disabled={importingId === b.id} style={fs.neutralButton}>
                      <FiUpload size={12} style={{ marginRight: 4, verticalAlign: -1 }} />{importingId === b.id ? 'Importing…' : 'Import Statement'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BankAccounts;
